import { useCallback, useEffect, useId, useState } from 'react';
import type { UnmatchedFilters, UnmatchedItem, UnmatchedList } from '../../../services/src/shared/api-types.ts';
import type { ScreenProps } from '../App.tsx';
import { ageInDays, day, daysAgo, money, num, when } from '../format.ts';
import { Empty, ErrorBanner, Loading } from '../ui.tsx';

type Side = 'line' | 'payment';

const TABS: { side: Side; label: string; hash: string }[] = [
  { side: 'line', label: 'Bank lines with no payment', hash: 'lines' },
  { side: 'payment', label: 'Payments with no bank line', hash: 'payments' },
];

const sideFromHash = (): Side => (location.hash.split('/')[2] === 'payments' ? 'payment' : 'line');

/** The "why" chip: read from the item's own fields, nothing guessed beyond them. */
function why(u: UnmatchedItem): string {
  if (u.kind === 'line') return u.amount < 0 ? 'Money in, nothing logged' : 'No payment logged';
  if (u.reason === 'no connected account') return 'No connected account';
  const age = ageInDays(u.date);
  if (/check|wire/i.test(u.method ?? '')) return age >= 1 ? `Not cleared after ${num(age)} ${age === 1 ? 'day' : 'days'}` : 'Not cleared yet';
  return 'No charge found';
}

/** Payment number and vendor; older snapshots only have "PMT-1 | Vendor". */
function paymentTitle(u: UnmatchedItem): string {
  const [name, vendor] = u.name ? [u.name, u.vendor ?? ''] : u.description.split(' | ');
  return [vendor || u.vendor, name].filter(Boolean).join(' · ');
}

export function Unmatched({ api }: ScreenProps) {
  const [side, setSide] = useState<Side>(sideFromHash);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [account, setAccount] = useState('');
  const [olderThan, setOlderThan] = useState('');
  const [list, setList] = useState<UnmatchedList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const ids = { search: useId(), account: useId(), age: useId(), panel: useId() };

  // Search waits for a pause in typing.
  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(() => {
    const filters: UnmatchedFilters = {
      kind: side,
      account: account || undefined,
      q: query || undefined,
      to: olderThan ? daysAgo(Number(olderThan)) : undefined,
    };
    let live = true;
    setLoading(true);
    setError(null);
    api.unmatched(filters).then(
      (r) => live && (setList(r), setLoading(false)),
      (e: Error) => live && (setError(e.message), setLoading(false)),
    );
    return () => {
      live = false;
    };
  }, [api, side, account, query, olderThan]);
  useEffect(load, [load]);

  const pick = (s: Side) => {
    setSide(s);
    history.replaceState(null, '', `#/unmatched/${TABS.find((t) => t.side === s)!.hash}`);
  };

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Unmatched</h1>
          <p className="lede">
            What the rules couldn't pair. Most need a payment logged in Salesforce or a call to the vendor.
            {list?.asOf ? ` As of ${when(list.asOf)}.` : ''}
          </p>
        </div>
      </header>

      <div role="tablist" aria-label="Side" className="tabs">
        {TABS.map((t) => (
          <button
            key={t.side}
            type="button"
            role="tab"
            id={`${ids.panel}-${t.side}`}
            aria-selected={side === t.side}
            aria-controls={ids.panel}
            className="tab"
            onClick={() => pick(t.side)}
          >
            {t.label}
            {list && <span className="mono">{num(list.counts[t.side])}</span>}
          </button>
        ))}
      </div>

      <div className="filters">
        <label className="field grow" htmlFor={ids.search}>
          Search
          <input id={ids.search} type="search" placeholder="Vendor, description or amount" value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
        <label className="field account" htmlFor={ids.account}>
          Account
          <select id={ids.account} value={account} onChange={(e) => setAccount(e.target.value)}>
            <option value="">All accounts</option>
            {(list?.accounts ?? []).map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </label>
        <label className="field age" htmlFor={ids.age}>
          Older than
          <select id={ids.age} value={olderThan} onChange={(e) => setOlderThan(e.target.value)}>
            <option value="">Any age</option>
            <option value="7">7 days</option>
            <option value="30">30 days</option>
          </select>
        </label>
      </div>

      <div role="tabpanel" id={ids.panel} aria-labelledby={`${ids.panel}-${side}`} aria-busy={loading}>
        {error && <ErrorBanner message={error} onRetry={load} />}
        {!list && !error && <Loading kind="table" />}
        {list && list.items.length === 0 && !error && (
          <Empty>{list.asOf ? 'Nothing unmatched for these filters.' : 'No matching run yet. The list fills in after the first sync with Salesforce.'}</Empty>
        )}
        {list && list.items.length > 0 && (
          <div className="card table-card">
            <table className="table min-640">
              <thead>
                <tr>
                  <th scope="col">Date</th>
                  <th scope="col">{side === 'line' ? 'Description' : 'Payment'}</th>
                  <th scope="col">Account</th>
                  <th scope="col">Why</th>
                  <th scope="col" className="num">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.items.map((u) => (
                  <tr key={`${u.kind}-${u.id}`}>
                    <td className="date">{day(u.date)}</td>
                    <td>
                      <div className="what">
                        <span>{u.kind === 'line' ? u.description : paymentTitle(u)}</span>
                        {u.kind === 'payment' && <span className="sub">{[u.trip, u.method].filter(Boolean).join(' · ')}</span>}
                      </div>
                    </td>
                    <td className="acct">{u.account || <span className="muted">No feed</span>}</td>
                    <td>
                      <span className="chip">{why(u)}</span>
                    </td>
                    <td className="num">{money(u.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {list && list.items.length < list.total && (
          <p className="small muted table-foot">
            Showing {num(list.items.length)} of {num(list.total)}. Search or pick an account to narrow it down.
          </p>
        )}
      </div>
    </div>
  );
}
